/**
 * The line and bar over a saved estimate's picture while its photoreal render
 * is on its way: the place in the queue, which picture is rendering and how
 * far, the time left, and how many pictures are in.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { RenderJob } from './types';

/** "under a minute" / "3 minutes". */
export function etaText(t: TFunction, sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec)) return null;
  if (sec < 60) return t('render.eta.underMinute');
  return t('render.eta.minutes', { count: Math.max(1, Math.round(sec / 60)) });
}

/** "4 min 12 s" / "48 s": a time taken, to the second. */
export function durationText(t: TFunction, sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null;
  const s = Math.round(sec);
  if (s < 60) return t('render.eta.exactSeconds', { seconds: s });
  return t('render.eta.exactMinutes', { minutes: Math.floor(s / 60), seconds: s % 60 });
}

export function progressLine(t: TFunction, job: RenderJob | null, flags: { delayed: boolean; checking: boolean }): string | null {
  if (flags.checking) return t('render.progress.checking');
  if (!job) return null;
  switch (job.status) {
    case 'awaiting-upload': return t('render.progress.awaitingUpload');
    case 'queued':
      if (flags.delayed) return t('render.progress.delayed');
      return job.queueAhead > 0 ? t('render.progress.queuedAhead', { count: job.queueAhead }) : t('render.progress.queuedFirst');
    case 'claimed':
    case 'rendering': {
      if (job.stage === 'uploading') return t('render.progress.uploading');
      const eta = etaText(t, job.etaSec);
      // The % said is the bar's: the whole job's, through every step (worker.js weighs them).
      const percent = Math.max(0, Math.min(99, job.percent));
      const withEta = (what: string) => (eta ? t('render.progress.withEta', { what, percent, eta }) : t('render.progress.withPercent', { what, percent }));
      // Before the first picture: the setup step under way (worker.js reports render_room.py's).
      if (!job.picture) {
        if (!job.step) return t('render.progress.claimed');
        const step = job.step.key === 'props' && job.step.count
          ? t('render.steps.propsCount', { index: job.step.index, count: job.step.count })
          : t(`render.steps.${job.step.key}`);
        return withEta(t('render.progress.setup', { step }));
      }
      const picture = t(`render.picturesIn.${job.picture.id}`);
      // Getting the picture ready: its textures, its scene, the graphics card's first-time warm-up.
      if (job.phase === 'loading' || job.phase === 'scene' || job.phase === 'kernels' || job.phase === 'denoising') {
        return withEta(t(`render.phases.${job.phase}`, { picture }));
      }
      return eta
        ? t('render.progress.rendering', { picture, percent, eta })
        : t('render.progress.renderingNoEta', { picture, percent });
    }
    case 'done': return t('render.progress.done');
    case 'failed':
      // The render limits (render-jobs-lib.php): stopped on purpose, not broken.
      if (job.error === 'limit-daily') return t('render.progress.limitDaily');
      if (job.error === 'superseded') return t('render.progress.superseded');
      return t('render.progress.failed');
    default: return null;
  }
}

export interface RenderProgressProps {
  job: RenderJob | null;
  /** Pictures in, of the whole set. */
  done: number;
  total: number;
  delayed: boolean;
  checking: boolean;
}

export const RenderProgress: React.FC<RenderProgressProps> = ({ job, done, total, delayed, checking }) => {
  const { t } = useTranslation('results');
  const line = progressLine(t, job, { delayed, checking });
  if (!line) return null;
  const percent = job?.status === 'done' ? 100 : Math.max(0, Math.min(100, job?.percent ?? 0));
  const failed = job?.status === 'failed';
  return (
    <div className={`rdock-progress${failed ? ' is-failed' : ''}`} role="status" aria-live="polite">
      <div className="rdock-line">{line}</div>
      {!failed && (
        <div className="rdock-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={t('render.progress.barLabel')}>
          <div className="rdock-bar-fill" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className="rdock-count">{t('render.progress.ready', { done, total })}</div>
    </div>
  );
};
